import { TAB_STALE_MS } from '../constants/session';
import {
  TAB_REGISTRY_KEY,
  TAB_ID_KEY,
  BROWSER_SESSION_KEY,
  BROWSER_SESSION_TOUCH_KEY,
  LAST_ACTIVITY_KEY,
  SESSION_WARNING_EXPIRES_KEY,
  TOKEN_KEY,
  clearAuthStorage,
} from '../utils/auth';

function readRegistry() {
  try {
    const raw = localStorage.getItem(TAB_REGISTRY_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeRegistry(registry) {
  localStorage.setItem(TAB_REGISTRY_KEY, JSON.stringify(registry));
}

function generateTabId() {
  return `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function generateSessionId() {
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function touchBrowserSession() {
  localStorage.setItem(BROWSER_SESSION_TOUCH_KEY, String(Date.now()));
}

/** Remove registry entries that have not sent a heartbeat recently. */
export function cleanTabRegistry() {
  const registry = readRegistry();
  const now = Date.now();
  const active = {};

  Object.entries(registry).forEach(([tabId, lastSeen]) => {
    if (now - lastSeen <= TAB_STALE_MS) {
      active[tabId] = lastSeen;
    }
  });

  writeRegistry(active);
  return active;
}

export function getActiveTabIds() {
  return Object.keys(cleanTabRegistry());
}

export function getOrCreateTabId() {
  let tabId = sessionStorage.getItem(TAB_ID_KEY);
  if (!tabId) {
    tabId = generateTabId();
    sessionStorage.setItem(TAB_ID_KEY, tabId);
  }
  return tabId;
}

export function registerTab(tabId = getOrCreateTabId()) {
  sessionStorage.setItem(TAB_ID_KEY, tabId);
  const registry = cleanTabRegistry();
  registry[tabId] = Date.now();
  writeRegistry(registry);
  touchBrowserSession();
  return tabId;
}

export function touchTab(tabId = sessionStorage.getItem(TAB_ID_KEY)) {
  if (!tabId) return;
  const registry = readRegistry();
  if (registry[tabId] !== undefined) {
    registry[tabId] = Date.now();
    writeRegistry(registry);
    touchBrowserSession();
  }
}

export function unregisterTab(tabId = sessionStorage.getItem(TAB_ID_KEY)) {
  if (!tabId) return getActiveTabIds().length;

  const registry = readRegistry();
  delete registry[tabId];
  writeRegistry(registry);

  return Object.keys(registry).length;
}

/** Remove tab-scoped session markers (call on explicit logout). */
export function clearTabSessionMarkers() {
  sessionStorage.removeItem(TAB_ID_KEY);
  sessionStorage.removeItem(BROWSER_SESSION_KEY);
}

export function updateLastActivity(timestamp = Date.now()) {
  localStorage.setItem(LAST_ACTIVITY_KEY, String(timestamp));
}

export function getLastActivity() {
  const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
  const parsed = raw ? Number(raw) : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

export function setWarningExpiresAt(timestamp) {
  if (timestamp) {
    localStorage.setItem(SESSION_WARNING_EXPIRES_KEY, String(timestamp));
  } else {
    localStorage.removeItem(SESSION_WARNING_EXPIRES_KEY);
  }
}

export function getWarningExpiresAt() {
  const raw = localStorage.getItem(SESSION_WARNING_EXPIRES_KEY);
  const parsed = raw ? Number(raw) : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Validates whether a stored token belongs to an active browser session.
 * Page refresh keeps sessionStorage, so the user stays logged in.
 * When the browser fully closes, sessionStorage is cleared; live tab
 * heartbeats stop and auth is cleared on the next visit.
 */
export function validateBrowserSession() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return false;

  const existingTabId = sessionStorage.getItem(TAB_ID_KEY);
  const tabSessionId = sessionStorage.getItem(BROWSER_SESSION_KEY);
  const browserSessionId = localStorage.getItem(BROWSER_SESSION_KEY);
  const lastTouch = Number(localStorage.getItem(BROWSER_SESSION_TOUCH_KEY) || 0);
  const anotherTabRecentlyActive =
    browserSessionId && Number.isFinite(lastTouch) && Date.now() - lastTouch <= TAB_STALE_MS;

  // Same-tab refresh — tab id in sessionStorage survives reload
  if (existingTabId && tabSessionId && tabSessionId === browserSessionId) {
    registerTab(existingTabId);
    return true;
  }

  // Same-tab refresh when only tab id persisted
  if (existingTabId) {
    if (browserSessionId) {
      sessionStorage.setItem(BROWSER_SESSION_KEY, browserSessionId);
    }
    registerTab(existingTabId);
    return true;
  }

  // New tab opened while another admin tab is still active
  if (anotherTabRecentlyActive && browserSessionId) {
    sessionStorage.setItem(BROWSER_SESSION_KEY, browserSessionId);
    registerTab();
    return true;
  }

  // Token present but browser was fully closed (no sessionStorage, no live tabs)
  clearAuthStorage();
  clearTabSessionMarkers();
  return false;
}

/** Called after a successful login to start a fresh browser session. */
export function establishSession() {
  const tabId = generateTabId();
  const sessionId = generateSessionId();
  sessionStorage.setItem(TAB_ID_KEY, tabId);
  sessionStorage.setItem(BROWSER_SESSION_KEY, sessionId);
  localStorage.setItem(BROWSER_SESSION_KEY, sessionId);
  writeRegistry({ [tabId]: Date.now() });
  touchBrowserSession();
  updateLastActivity();
  setWarningExpiresAt(null);
}
